import { CommonModule, DatePipe } from "@angular/common";
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { FormGroup, ReactiveFormsModule } from "@angular/forms";
import { OrderLineItemDetailService } from "../../../../core/services/order-line-item-detail.service";
import { ShippingMethod } from "../../../../core/models/shared/shippingMethod";
import { DatePickerModule } from "primeng/datepicker";
import { SelectChangeEvent, SelectModule } from "primeng/select";
import { InputTextModule } from "primeng/inputtext";
import { DropdownModule } from "primeng/dropdown";
import { ToggleSwitchModule } from "primeng/toggleswitch";
import {
  computeMinDate,
  parseMMDDYYYY,
} from "../../../../core/utilities/mindate-function";

@Component({
  selector: "jni-order-line-item-details",
  templateUrl: "./order-line-item-details.component.html",
  styleUrls: ["./order-line-item-details.component.scss"],
  providers: [DatePipe],
  imports: [
    DatePickerModule,
    CommonModule,
    ReactiveFormsModule,
    SelectModule,
    InputTextModule,
    DropdownModule,
    ToggleSwitchModule,
  ],
})
export class OrderLineItemDetailsComponent implements OnChanges {
  @Input() parent!: FormGroup;
  @Input() shippingProductsList: ShippingMethod[] = [];
  @Input() customerId = "";
  @Input() headerShipDate = "";
  @Input() headerInhandDate = "";
  @Input() formErrors!: Record<string, string>;
  @Input() orderType = "";
  sharedMonthForView = new Date();

  @Output() validationErrorEvent = new EventEmitter();

  @Input() templateService = false;

  shipDateCurrentdate!: Date;
  inHandDateCurrentdate!: Date;

  shipDate!: string;
  inHandDate!: string;
  shipdate = "";

  @ViewChild("nameInput") inputNameRef!: ElementRef;
  @ViewChild("nameInput1") inputNameRef1!: ElementRef;
  constructor(
    private _orderLineItemDetailService: OrderLineItemDetailService,
    public datepipe: DatePipe
  ) {}

  /**
   * Returns true when the currently selected shipping method is a
   * Nadel freight account (e.g. FedEx / UPS Nadel Account). In that case
   * the supplier account number must be auto-populated and non-editable.
   */
  get isNadelFreightAccount(): boolean {
    const shippingMethodCode = this.parent?.get([
      "detail",
      "shippingMethod",
    ])?.value;
    if (!shippingMethodCode || !this.shippingProductsList?.length) {
      return false;
    }
    const method = this.shippingProductsList.find(
      x => x.modeCodeD365 === shippingMethodCode
    );

    if (method?.useFreightAccountD365?.toLowerCase() !== "yes") {
      return false;
    }

    // Client requirement: only hide/lock the account number when the
    // shipping method is UPS or FedEx (Nadel freight accounts).
    const description = method?.modeDescriptionD365?.toLowerCase() ?? "";
    return description.includes("ups") || description.includes("fedex");
  }

  ngOnChanges(changes: SimpleChanges) {
    let candidate: Date | null = null;
    const shipDateVal = parseMMDDYYYY(
      this.parent.get(["detail", "shipDate"])?.value
    );
    const inHandDateVal = parseMMDDYYYY(
      this.parent.get(["detail", "inHandDate"])?.value
    );
    if (shipDateVal) {
      this.shipDateCurrentdate = computeMinDate(shipDateVal);
    }
    if (shipDateVal && inHandDateVal) {
      candidate =
        new Date(
          shipDateVal.getFullYear(),
          shipDateVal.getMonth(),
          shipDateVal.getDate()
        ) <
        new Date(
          inHandDateVal.getFullYear(),
          inHandDateVal.getMonth(),
          inHandDateVal.getDate()
        )
          ? shipDateVal
          : inHandDateVal;
      this.inHandDateCurrentdate = computeMinDate(candidate);
    } else if (!shipDateVal && inHandDateVal) {
      this.shipDateCurrentdate = computeMinDate(inHandDateVal);
      this.inHandDateCurrentdate = computeMinDate(inHandDateVal);
    } else if (shipDateVal && !inHandDateVal) {
      this.shipDateCurrentdate = computeMinDate(shipDateVal);
      this.inHandDateCurrentdate = computeMinDate(shipDateVal, false);
    }
    this.orderTypeChanges(changes);
  }

  orderTypeChanges(changes: SimpleChanges) {
    if (changes["orderType"] && this.orderType == "inventory-purchase") {
      this.inventoryPurchaseInHandsDate();
    }
  }

  inventoryPurchaseInHandsDate() {
    this.inHandDateCurrentdate.setDate(new Date().getDate() + 1);
  }

  setMindate() {
    if (!this.shipDateCurrentdate) {
      this.shipDateCurrentdate = computeMinDate();
    }
  }

  setMinInHandsdate() {
    if (!this.inHandDateCurrentdate) {
      this.inHandDateCurrentdate = computeMinDate();
    }
  }

  onShipDateSelection(event: Date) {
    //this.shipDate = new Date(event).toLocaleDateString();
    const shipDate = new Date(event);
    this.sharedMonthForView = shipDate;
    this.shipDate = this.datepipe.transform(shipDate, "MM/dd/yyyy") ?? "";
    if (this.shipDate) {
      this.inHandDateCurrentdate = computeMinDate(this.shipDate, false);
    } else {
      this.inHandDateCurrentdate = computeMinDate();
    }
    this.parent.patchValue({
      detail: {
        shipDate: this.shipDate,
      },
    });
  }

  clearShippingMethod() {
    this.parent.patchValue({
      detail: {
        accountNumberD365: "",
      },
    });
  }

  onInHandDateSelection(event: Date) {
    //this.inHandDate = new Date(event).toLocaleDateString();
    const inHandDate = new Date(event);
    this.inHandDate = this.datepipe.transform(inHandDate, "MM/dd/yyyy") ?? "";
    this.parent.patchValue({
      detail: {
        inHandDate: this.inHandDate,
      },
    });
  }

  clearInHandDateAndShipDate() {
    this.parent.patchValue({
      detail: {
        inHandDate: "",
        shipDate: "",
      },
    });

    // (<HTMLInputElement>document.getElementById('shipdate')).value = '';
    // this.inputNameRef.nativeElement.onblur();
    // (<HTMLInputElement>document.getElementById('inhanddate')).value = '';
    // this.inputNameRef1.nativeElement.onblur();
  }

  onShippingMethodSelect(shippingMethodCode: SelectChangeEvent) {
    const target = shippingMethodCode.value;
    const index = this.shippingProductsList.findIndex(
      x => x.modeCodeD365 == target
    );

    if (index > -1) {
      const selectedShippingMethod = this.shippingProductsList[index];
      if (
        selectedShippingMethod.useFreightAccountD365?.toLowerCase() == "yes"
      ) {
        this.parent.patchValue({
          detail: {
            accountNumberD365: selectedShippingMethod.jniFreightAcctNumD365,
          },
        });
      } else {
        this._orderLineItemDetailService
          .getClientFreightAccountNumber(this.customerId)
          .subscribe({
            next: res => {
              if (res.data) {
                this.parent.patchValue({
                  detail: {
                    accountNumberD365: res.data,
                  },
                });
              } else {
                this.parent.patchValue({
                  detail: {
                    accountNumberD365: "",
                  },
                });
              }
            },
            error: () => {
              this.parent.patchValue({
                detail: {
                  accountNumberD365: "",
                },
              });
            },
          });
      }
    }
  }

  applyAccountLengthValidation(event: FocusEvent) {
    // Do not mutate the value when the field is auto-populated with a
    // Nadel freight account and must remain read-only.
    if (this.isNadelFreightAccount) {
      return;
    }
    const target = event.target as HTMLInputElement;
    if (target?.value) {
      this.parent.patchValue({
        detail: {
          accountNumberD365: target?.value.substring(0, 40),
        },
      });
    }
  }

  logValidationErrors() {
    this.validationErrorEvent.emit();
  }

  onClearShippingMethodSelection() {
    this.parent.patchValue({
      detail: {
        accountNumberD365: "",
      },
    });
  }
}
